

import { useState } from 'react';
import { X, MessageCircle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "../../../../services/api";
import { getAssetUrl, getTimeAgo } from '../../../../services/helper';
import { comment, Dove } from '../../../assets/images';

// ─── Helpers ────────────────────────────────────────────────────────────────
function getInitials(name) {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function CircleAvatar({ name, color = "#1F3A60", size = 44 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontWeight: 700,
        fontSize: size * 0.34,
        flexShrink: 0,
      }}
    >
      {getInitials(name)}
    </div>
  );
}

const API_ROOT = (process.env.REACT_APP_API_URL || "http://localhost:5000/api").replace("/api", "");

function Avatar({ name, avatar, color }) {
  return (
    <div
      className="w-12 h-12 sm:w-[54px] sm:h-[54px] rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center"
      style={{ backgroundColor: color || '#1F3A60' }}
    >
      {avatar ? (
        <img
          src={avatar.startsWith("/uploads") ? `${API_ROOT}${avatar}` : avatar}
          alt={name}
          className="w-full h-full object-cover rounded-full"
        />
      ) : (
        <span className="text-white font-bold text-lg">{getInitials(name)}</span>
      )}
    </div>
  );
}
  const isImage = (p) => /\.(jpe?g|png|gif|webp|jfif|avif|svg)$/i.test(p);

function AvatarSmall({ name, avatar }) {
  console.log({ name, avatar });
  return (
    <div
      className={`w-8 h-8 sm:w-[34px] sm:h-[34px] rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center`}

    >
      {/* {avatar ? ( */}
      <img
        src={avatar.startsWith("/uploads") ? `${API_ROOT}${avatar}` : avatar}
        alt={name}
        className="w-full h-full object-cover rounded-full"
      />
      {/* ) : (
        <span className="text-white font-bold text-lg">{getInitials(name)}</span>
      )} */}
    </div>
  );
}

// ─── Comment Item ───────────────────────────────────────────────────────────
function CommentItem({ comment }) {
  return (
    <div className="mb-6">
      <div className="flex gap-3">
        <AvatarSmall name={comment?.author?.name} avatar={comment?.author?.avatar} />

        {/* <CircleAvatar name={comment.name || 'User'} color={comment.avatarColor} size={40} /> */}
        <div className="flex-1">
          <div className="flex justify-between mb-1">
            <span className="font-semibold text-[14px] text-gray-900">{comment?.author?.name}</span>
            <span className="text-xs text-gray-400">{getTimeAgo(comment?.createdAt)}</span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed mb-3">{comment?.content}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Modal ─────────────────────────────────────────────────────────────
export default function PreviewModal({ item, onClose }) {
  const queryClient = useQueryClient();
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState("");

  if (!item) return null;

  // Fetch Comments
  const {
    data: commentsResponse,
    isLoading: commentsLoading,
    refetch: loadComments
  } = useQuery({
    queryKey: ["post-comments", item.id],
    queryFn: () => api.getComments(item.id),
    enabled: !!item.id,           // Only enable if we have a valid post id
    staleTime: 1000 * 60 * 5,     // Optional: cache for 5 minutes
  });

  const comments = commentsResponse?.comments || [];

  // Post Comment Mutation
  const postCommentMutation = useMutation({
    mutationFn: (newComment) => api.postComment(item.id, newComment),
    onSuccess: () => {
      queryClient.invalidateQueries(["post-comments", item.id]);
      setCommentText("");
    },
    onError: (err) => {
      alert("Failed to post comment: " + (err?.message || "Unknown error"));
    },
  });

  const handlePostComment = () => {
    if (!commentText.trim()) return;
    postCommentMutation.mutate({ text: commentText.trim() });
  };

  const handleToggleComments = async () => {
    const willShow = !showComments;
    setShowComments(willShow);

    if (willShow) {
      await loadComments();   // Force fetch when opening comments
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-4"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-3xl w-full max-w-[680px] max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-center justify-between border-b">
          <div className="flex items-center gap-3">
            <Avatar
              name={item.name}
              avatar={item.authorAvatar || item.avatar}
              color={item.avatarColor || '#1F3A60'}
            />
            <div>
              <div className="font-bold text-[16px]">{item.name}</div>
              <div className="text-xs text-gray-400">
                {item.time || item.created_at} • {item.category || 'Community'}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl">
            <X size={22} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        {item.post_type == "normal" && <>  
        <div className="flex-1 p-4 space-y-6">
          {/* {item.title && <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>} */}

          <p className="text-[15px] leading-relaxed text-gray-700">
            {item.content}
          </p>


          {item?.attachment && (
            <div className="relative">
              {/\.(jpe?g|png|gif|webp|avif|svg)$/i.test(item?.attachment) ? (
                <img
                  src={
                    item?.attachment?.startsWith("/uploads")
                      ? `${API_ROOT}${item.attachment}`
                      : item.attachment
                  }
                  alt=""
                  className="w-full max-h-[280px] sm:max-h-[360px] md:max-h-[420px] object-cover"
                />
              ) : (
                <video
                  src={
                    item?.attachment?.startsWith("/uploads")
                      ? `${API_ROOT}${item.attachment}`
                      : item.attachment
                  }
                  controls
                  className="w-full max-h-[280px] sm:max-h-[360px] md:max-h-[420px] object-cover"
                />
              )}
            </div>
          )}
        </div>
        </>}
          {item.post_type == "testimony" && <>     <div className="px-6 pt-5 grid grid-cols-2 gap-4 sm:gap-6">
          {/* Before */}
          <div>
            <p className="text-[14px] sm:text-[18px] font-bold text-[#1F3A60] font-body mb-2">Before</p>
            <p className="text-[14px] sm:text-[16px] text-gray-600 leading-[1.6] font-body whitespace-pre-wrap">
              {item.before_text}
            </p>
            {item.before_attachment && (
              <div className="mt-3 rounded-xl overflow-hidden">
                {isImage(item.before_attachment) ? (
                  <img src={getAssetUrl(item.before_attachment)} alt="" className="w-full max-h-[200px] object-cover rounded-xl" />
                ) : (
                  <video src={getAssetUrl(item.before_attachment)} controls className="w-full max-h-[200px] rounded-xl" />
                )}
              </div>
            )}
          </div>

          {/* After */}
          <div>
            <p className="text-[14px] sm:text-[18px] font-bold text-[#1F3A60] font-body mb-2">After</p>
            <p className="text-[14px] sm:text-[16px] text-gray-600 leading-[1.6] font-body whitespace-pre-wrap">
              {item.after_text || ""}
            </p>
            {item.after_attachment && (
              <div className="mt-3 rounded-xl overflow-hidden">
                {isImage(item.after_attachment) ? (
                  <img src={getAssetUrl(item.after_attachment)} alt="" className="w-full max-h-[200px] object-cover rounded-xl" />
                ) : (
                  <video src={getAssetUrl(item.after_attachment)} controls className="w-full max-h-[200px] rounded-xl" />
                )}
              </div>
            )}
          </div>
        </div></>}

        {/* Actions Bar */}

        <div className="px-6 py-5 flex items-center gap-6">
          <button className="flex items-center gap-2.5 transition-all">
            <img
              src={Dove}
              alt="Bless"
              className={`w-[30px] h-[30px] object-contain transition-all duration-300 
                grayscale
              `}
            />
            <span className="text-[14px] font-medium text-gray-500">
              {item?.likes} Bless
            </span>
          </button>

          <button
            onClick={handleToggleComments}
            className="flex items-center gap-2.5 transition-all"
          >
            <img
              src={comment}
              alt="Comments"
              className="w-[30px] h-[30px] object-contain"
            />

            <span
              className={`text-[14px] font-medium ${showComments ? "text-blue-600" : "text-gray-500"
                }`}
            >
              {item?.comments} Comments
            </span>
          </button>
        </div>
        {/* Comments Section */}
        {showComments && (
          <div className="px-6 pt-5 pb-6 border-t bg-white overflow-y-auto ">
            {/* Comment Input */}
            {/* <div className="flex gap-3 mt-6 mb-8">
              <CircleAvatar name="You" color="#6366F1" size={40} />
              <div className="flex-1 flex">
                <input
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handlePostComment()}
                  placeholder="Write a comment..."
                  className="flex-1 bg-gray-100 border border-gray-200 rounded-full px-5 py-3 text-[15px] focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={handlePostComment}
                  disabled={!commentText.trim() || postCommentMutation.isPending}
                  className="ml-2 px-6 bg-[#1E2D4F] text-white rounded-full font-medium disabled:opacity-60"
                >
                  {postCommentMutation.isPending ? "Posting..." : "Post"}
                </button>
              </div>
            </div> */}

            {/* Comments List */}
            {commentsLoading ? (
              <div className="text-center py-8 text-gray-500">Loading comments...</div>
            ) : comments.length > 0 ? (
              comments.map((comment) => (
                <CommentItem key={comment.id} comment={comment} />
              ))
            ) : (
              <div className="text-center py-10 text-gray-500">
                No comments yet.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}